import { useCallback, useEffect, useRef } from 'react'
import { PDF_GROUNDING_RULE } from '../../lib/pdf-grounding.js'
import { generateTranscriptId } from '../../lib/transcript-append.js'
import { logger } from '../../lib/logger-client.js'
import { trackEvent } from '../../lib/analytics.js'
import { LiveConnectionState, type TranscriptItem } from 'types'
import type { LiveSessionPolicy } from '../../types/live-policy.js'
import type {
  LiveRuntimeController,
  LiveRuntimeOwnershipState,
  LiveTransportState,
} from '../../live/liveRuntimeController.js'
import { handleWebcamCaptureDirect, handleScreenCaptureDirect } from '../../lib/services/visual-tools-client.js'
import { isVisualRecallQuestion, LIVE_WEBCAM_INTENT_REGEX, LIVE_SCREEN_INTENT_REGEX, deniesLiveVisualShare } from '../../lib/live-visual-handoff.js'
import { liveRuntimeTrace, sessionIdPrefix } from '../../lib/live-runtime-trace.js'
import { cleanupLiveSessionGenerationState } from '../../lib/composer-generation-cleanup.js'
import {
  detectLiveFirstForcedGate,
  isLiveFirstFunnelEnabled,
  LIVE_FIRST_FALLBACK_MESSAGE_KEY,
  LIVE_FIRST_SEND_STATUS_KEYS,
} from '../../lib/live-first-funnel.js'
import { useLocaleContext } from '../../context/LocaleContext.js'
import { resolveStickyReplyLanguage } from '../../lib/infer-language-preference.js'
import {
  customerReplyLanguage,
  intakeLanguageForTurn,
  languageAfterResearch,
  type ReplyLanguage,
} from '../../lib/reply-language.js'
import { replyTimestampAfter } from '../../lib/live-transcript-reconciler.js'
import { decideCompanyBinding } from '../../lib/company-binding.js'
import { createSendIdempotencyKey, shouldAcceptSend } from '../../lib/send-idempotency.js'
import { logTurnObservability } from '../../lib/live-turn-observability.js'
import { isDiscoveryReportRequest, isExplicitBookingAction } from '../../core/agents/consultation-intent-router.js'
import { deterministicNbIdentityReply } from '../../core/agents/utils/nb-identity-reply.js'
import {
  getSessionFactsReadiness,
  isRemainingDiscoveryAsk,
  formatRemainingDiscoveryReply,
} from '../../core/session/session-facts.js'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { appendBookingReply } from '../../lib/transcript-side-effects.js'
import {
  advanceWorkshopIntake,
  companyNameFromEmail,
  intakeFieldsFromResearchContext,
  intakeFieldsFromSessionFacts,
  intakePathForTurn,
  shouldPersistWorkshopIntake,
  WORKSHOP_CUSTOMER_ERROR,
  type WorkshopIntake,
} from '../../core/workshop-pack/intake-conversation.js'
import { appendCanonicalExchange, storeExchangeThenRefresh } from '../../lib/append-canonical-exchange.js'
import { submitWorkshopIntake } from '../../lib/submit-workshop-intake.js'
import { createCanonicalStoreQueue } from '../../lib/canonical-store-queue.js'
import { LIVE_SCREEN_FRAME_DIRECTIVE, LIVE_WEBCAM_FRAME_DIRECTIVE } from '../../lib/live-visual-directive.js'

const SCREEN_INTENT_REGEX = LIVE_SCREEN_INTENT_REGEX

function buildLiveVisualDirective(text: string): string {
  const asksScreen = SCREEN_INTENT_REGEX.test(text)
  const asksWebcam = LIVE_WEBCAM_INTENT_REGEX.test(text)
  if (asksScreen) {
    return LIVE_SCREEN_FRAME_DIRECTIVE
  }
  if (asksWebcam) {
    return LIVE_WEBCAM_FRAME_DIRECTIVE
  }
  return ''
}

function getDeterministicVisualFallbackReason(
  text: string,
  isWebcamActive: boolean,
  isScreenShareActive: boolean
): 'screen_intent_no_frame' | 'webcam_intent_no_frame' | null {
  // Recall prompts mention webcam/screen historically but do not require a live frame.
  if (isVisualRecallQuestion(text)) return null
  if (deniesLiveVisualShare(text)) return null
  const asksScreen = SCREEN_INTENT_REGEX.test(text)
  const asksWebcam = LIVE_WEBCAM_INTENT_REGEX.test(text)
  if (asksScreen && !isScreenShareActive) return 'screen_intent_no_frame'
  if (asksWebcam && !isWebcamActive) return 'webcam_intent_no_frame'
  return null
}

interface MessageFile {
  mimeType: string
  data: string
  type?: string
  url?: string
  name?: string
  source?: 'webcam' | 'screenshare' | 'upload' | 'user_upload'
  textContent?: string
}

interface SendMessageOptions {
  skipUserAppend?: boolean
  historyOverride?: TranscriptItem[]
  timestampOverride?: Date
  skipResearch?: boolean
}

interface UseMessageComposerProps {
  sessionId: string
  connectionState: LiveConnectionState
  aiBrainRef: React.MutableRefObject<any>
  liveServiceRef: React.MutableRefObject<LiveRuntimeController | null>
  transcriptRef: React.MutableRefObject<TranscriptItem[]>
  latestWebcamFrameRef: React.MutableRefObject<string | null>
  latestScreenFrameRef: React.MutableRefObject<string | null>
  restTurnOwnerRef?: React.MutableRefObject<{ loadingId: string; startedAt: number } | null>
  userProfileRef: React.MutableRefObject<any>
  abortControllerRef: React.MutableRefObject<AbortController | null>
  currentReasoning: string
  currentReasoningRef: React.MutableRefObject<string>
  currentLoadingIdRef: React.MutableRefObject<string | null>
  toolsForMessageRef: React.MutableRefObject<Array<any>>
  toolIndexByIdRef: React.MutableRefObject<Record<string, number>>
  inFlightSendKeysRef: React.MutableRefObject<Set<string>>
  lastSendRef: React.MutableRefObject<{ key: string; at: number } | null>
  lastTextInputRef: React.MutableRefObject<{ at: number } | null>
  semanticShapeRef: React.MutableRefObject<any>
  textContentRef: React.MutableRefObject<string | undefined>
  weatherDataRef: React.MutableRefObject<any>
  chartDataRef: React.MutableRefObject<any>
  mapDataRef: React.MutableRefObject<any>
  isWebcamActive: boolean
  isScreenShareActive: boolean
  performResearch: (text: string) => Promise<any> | void
  persistMessageToServer: (
    sessionId: string,
    role: 'user' | 'model',
    content: string,
    timestamp: Date,
    attachment?: { mimeType: string; data: string; name?: string },
    source?: 'text_chat' | 'voice_transcript'
  ) => Promise<void>
  setTranscript: React.Dispatch<React.SetStateAction<TranscriptItem[]>>
  setBackendStatus: React.Dispatch<
    React.SetStateAction<{
      mode: 'idle' | 'agents' | 'fallback' | 'voice'
      message: string
      severity: 'info' | 'warn' | 'error'
    }>
  >
  applyServerTurnResult: (params: {
    transcript: TranscriptItem[]
    backendStatus?: {
      mode: 'idle' | 'agents' | 'fallback' | 'voice'
      message: string
      severity: 'info' | 'warn' | 'error'
    }
    projectionEnvelope?: unknown
  }) => void
  setVisualState: React.Dispatch<React.SetStateAction<any>>
  setActiveTools: React.Dispatch<React.SetStateAction<any[]>>
  setCurrentReasoning: React.Dispatch<React.SetStateAction<string>>
  setIsThinking: (value: boolean) => void
  setLivePolicy: React.Dispatch<React.SetStateAction<LiveSessionPolicy>>
  inferAffectiveStateFromText: (
    text: string,
    current: LiveSessionPolicy['affectiveState']
  ) => LiveSessionPolicy['affectiveState']
  inferLanguagePreference: (text: string, fallback: string) => string
  showToast: (message: string, type: 'success' | 'error' | 'info') => void
  handleGenerateDiscoveryReport: (summaryAgentOutput?: { executiveSummary?: string; keyFindings?: any; recommendedSolution?: string; solutionRationale?: string; expectedROI?: string; pricingBallpark?: string; nextSteps?: string; multimodalInteractionSummary?: any }, source?: 'click' | 'auto' | 'live-tool') => void | Promise<boolean | void>
  handleConnect?: (opts?: { audio?: boolean }) => Promise<void>
  beginGateTurn?: () => string
  onConciergeGateBlocked?: () => void
}

function isLiveRealtimeMediaMime(mimeType?: string): boolean {
  if (!mimeType) return false
  if (mimeType.startsWith('image/')) return true
  if (mimeType.startsWith('audio/')) return true
  if (mimeType.startsWith('video/')) return true
  if (mimeType.includes('pcm') || mimeType.includes('rate=')) return true
  return false
}

function isVisualCheckPrompt(text: string): boolean {
  const normalized = text.trim().toLowerCase()
  return (
    normalized.includes('can you see me') ||
    normalized.includes('do you see me') ||
    normalized.includes('what do you see') ||
    normalized.includes('can you see') ||
    normalized.includes('look at me') ||
    normalized.includes('what did you see') ||
    normalized.includes('summarize everything you saw') ||
    normalized.includes('summarize what you saw') ||
    normalized.includes('everything you saw') ||
    normalized.includes('what was on my screen') ||
    normalized.includes('what was on the screen') ||
    normalized.includes('what did we discuss and what you saw') ||
    SCREEN_INTENT_REGEX.test(text) ||
    LIVE_WEBCAM_INTENT_REGEX.test(text)
  )
}

function isLiveRuntimeConnected(
  connectionState: LiveConnectionState,
  liveService: LiveRuntimeController | null
): boolean {
  // CONNECTED is authoritative even if liveServiceRef is briefly null during a
  // controller swap. Requiring the service first let REST/SSE start while Live
  // still owned the socket (typed/PDF dual-writer race).
  if (connectionState === LiveConnectionState.CONNECTED) return true
  if (!liveService) return false

  try {
    const transportState =
      typeof liveService.getRealtimeTransportState === 'function'
        ? liveService.getRealtimeTransportState()
        : null

    if (transportState?.isConnected === true || transportState?.isSessionReady === true) {
      return true
    }

    return typeof liveService.getIsSessionReady === 'function'
      ? liveService.getIsSessionReady() === true
      : false
  } catch {
    return false
  }
}

function isLiveRuntimeBlockingRest(
  connectionState: LiveConnectionState,
  liveService: LiveRuntimeController | null
): boolean {
  if (connectionState === LiveConnectionState.CONNECTING) return true
  return isLiveRuntimeConnected(connectionState, liveService)
}

function isLiveMicOpen(liveService: LiveRuntimeController | null): boolean {
  try {
    return typeof liveService?.getIsAudioInputActive === 'function'
      ? liveService.getIsAudioInputActive() === true
      : false
  } catch {
    return false
  }
}

function composerFileLabel(file: MessageFile): string {
  const name = file.name?.trim()
  if (name) return name
  if (file.mimeType === 'application/pdf' || file.mimeType.includes('pdf')) return 'document.pdf'
  if (file.mimeType.startsWith('image/')) return 'image'
  return 'attachment'
}

function isPdfMime(mimeType?: string): boolean {
  if (!mimeType) return false
  return mimeType === 'application/pdf' || mimeType.toLowerCase().includes('pdf')
}

function liveOwnedTurnText(text: string, file?: MessageFile): string {
  const trimmed = text.trim()
  if (!file) return trimmed
  const extracted = file.textContent?.trim()
  const attached = `Attached file: ${composerFileLabel(file)}`
  const fileBlock = extracted
    ? isPdfMime(file.mimeType)
      ? `[PDF CONTENT: ${extracted}]\n${PDF_GROUNDING_RULE}`
      : `[FILE: ${composerFileLabel(file)} (${file.mimeType})]\n${extracted}`
    : attached
  return trimmed ? `${trimmed}\n\n${fileBlock}` : fileBlock
}

function getLiveRuntimeDiagnostics(
  connectionState: LiveConnectionState,
  liveService: LiveRuntimeController | null
) {
  let transportState: LiveTransportState | { error: string } | null = null
  let isSessionReady: boolean | undefined
  let ownershipState: LiveRuntimeOwnershipState | { error: string } | null = null

  try {
    transportState =
      typeof liveService?.getRealtimeTransportState === 'function'
        ? liveService.getRealtimeTransportState()
        : null
  } catch (error) {
    transportState = { error: error instanceof Error ? error.message : String(error) }
  }

  try {
    isSessionReady =
      typeof liveService?.getIsSessionReady === 'function'
        ? liveService.getIsSessionReady() === true
        : undefined
  } catch {
    isSessionReady = false
  }

  try {
    ownershipState =
      typeof liveService?.getRuntimeOwnershipState === 'function'
        ? liveService.getRuntimeOwnershipState()
        : null
  } catch (error) {
    ownershipState = { error: error instanceof Error ? error.message : String(error) }
  }

  return {
    connectionState,
    hasLiveService: Boolean(liveService),
    transportState,
    isSessionReady,
    ownershipState,
  }
}

function liveRuntimeOwnsTurns(liveService: LiveRuntimeController | null): boolean {
  try {
    return typeof liveService?.getRuntimeOwnershipState === 'function'
      ? liveService.getRuntimeOwnershipState()?.ownsTurns === true
      : false
  } catch {
    return false
  }
}

export function useMessageComposer({
  sessionId,
  connectionState,
  liveServiceRef,
  transcriptRef,
  restTurnOwnerRef,
  userProfileRef,
  abortControllerRef,
  currentLoadingIdRef,
  inFlightSendKeysRef,
  lastSendRef,
  lastTextInputRef,
  isWebcamActive,
  isScreenShareActive,
  performResearch,
  persistMessageToServer,
  setTranscript,
  setBackendStatus,
  setIsThinking,
      setLivePolicy,
      inferAffectiveStateFromText,
      inferLanguagePreference,
      showToast,
  handleGenerateDiscoveryReport,
  handleConnect,
  beginGateTurn,
}: UseMessageComposerProps) {
  const { t, setLocale, languageCode } = useLocaleContext()
  const workshopIntakeRef = useRef<WorkshopIntake | null>(null)
  const languageStreakRef = useRef<{ code: string; count: number }>({ code: 'en-US', count: 0 })
  // P3: Report timeout ref — cancel on unmount or new turn
  const reportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connectionStateRef = useRef(connectionState)
  connectionStateRef.current = connectionState
  const lastLiveHeldAtRef = useRef(0)
  void inferLanguagePreference
  if (
    isLiveRuntimeBlockingRest(connectionState, liveServiceRef.current) ||
    isLiveMicOpen(liveServiceRef.current)
  ) {
    lastLiveHeldAtRef.current = Date.now()
  }

  // Browser writes to the canonical session, in send order (sess_ea8bf19d, 17.10).
  const canonicalStoreQueueRef = useRef(createCanonicalStoreQueue())

  const handleSendMessage = useCallback(
    async (text: string, file?: MessageFile, opts?: SendMessageOptions) => {
      // P3: Cancel any pending discovery report from previous turn
      if (reportTimeoutRef.current) {
        clearTimeout(reportTimeoutRef.current)
        reportTimeoutRef.current = null
      }
      const dedupeKey = createSendIdempotencyKey(text || '', file)
      const nowMs = Date.now()
      if (
        !shouldAcceptSend({
          key: dedupeKey,
          now: nowMs,
          last: lastSendRef.current,
          inFlight: inFlightSendKeysRef.current,
        })
      ) {
        logger.warn('[App] Dropping duplicate send', { dedupeKey })
        return
      }
      lastSendRef.current = { key: dedupeKey, at: nowMs }
      lastTextInputRef.current = { at: nowMs }
      inFlightSendKeysRef.current.add(dedupeKey)
      try {

      // Track first chat message for analytics.
      const isFirstUserTurn = transcriptRef.current.filter(t => t.role === 'user').length === 0
      if (isFirstUserTurn) trackEvent('chat_started', { sessionId })

      // A typed sentence switches the UI even when the plate locked a locale.
      // Voice keeps the streak so one ASR fragment does not thrash.
      const voiceTurn =
        connectionState === LiveConnectionState.CONNECTED &&
        liveServiceRef.current?.getIsAudioInputActive() === true
      const currentReply: ReplyLanguage = languageCode.startsWith('en')
        ? 'en'
        : languageCode.startsWith('nn')
          ? 'nn'
          : 'nb'
      const turnReply: ReplyLanguage = voiceTurn
        ? currentReply
        : customerReplyLanguage(text, currentReply)
      const uiNow = turnReply === 'en' ? 'en' : 'nb'
      if (!voiceTurn) {
        const code = turnReply === 'en' ? 'en-US' : turnReply === 'nn' ? 'nn-NO' : 'nb-NO'
        const currentUi = currentReply === 'en' ? 'en' : 'nb'
        if (uiNow !== currentUi) setLocale(uiNow, { explicit: true })
        languageStreakRef.current = { code, count: 0 }
      }

      const skipUserAppend = opts?.skipUserAppend === true
      const userTimestamp = opts?.timestampOverride ?? new Date()

      // Visual check prompt: attach fresh snapshot analysis directly to the message
      // so the REST agent receives visual context even when no Live session is active.
      let enhancedText = text
      if ((isWebcamActive || isScreenShareActive) && sessionId && isVisualCheckPrompt(text) && connectionState !== LiveConnectionState.CONNECTED) {
        try {
          const prefersScreen = SCREEN_INTENT_REGEX.test(text)
          const prefersWebcam = LIVE_WEBCAM_INTENT_REGEX.test(text)
          let freshAnalysis: { success: boolean; analysis?: string } | undefined
          let visualModality: 'webcam' | 'screen' | undefined
          if (prefersScreen && isScreenShareActive) {
            freshAnalysis = await handleScreenCaptureDirect({ sessionId, focus_prompt: 'Describe screen share frame.', summaryOnly: true })
            visualModality = 'screen'
          } else if (prefersWebcam && isWebcamActive) {
            freshAnalysis = await handleWebcamCaptureDirect({ sessionId, focus_prompt: 'Describe webcam frame.', summaryOnly: true })
            visualModality = 'webcam'
          } else if (isWebcamActive && !isScreenShareActive) {
            freshAnalysis = await handleWebcamCaptureDirect({ sessionId, focus_prompt: 'Describe webcam frame.', summaryOnly: true })
            visualModality = 'webcam'
          } else if (isScreenShareActive && !isWebcamActive) {
            freshAnalysis = await handleScreenCaptureDirect({ sessionId, focus_prompt: 'Describe screen share frame.', summaryOnly: true })
            visualModality = 'screen'
          }
          if (freshAnalysis?.success && freshAnalysis.analysis && visualModality) {
            enhancedText += `\n\n[Visual Context: ${freshAnalysis.analysis}]`
          }
        } catch (err) {
          logger.warn('[useMessageComposer] Fresh snapshot failed', { error: String(err) })
        }
      }

      const isImage = file?.mimeType.startsWith('image/')
      const userItem: TranscriptItem = {
        id: generateTranscriptId('user'),
        role: 'user',
        text: text,
        timestamp: userTimestamp,
        isFinal: true,
        status: 'complete',
        ...(file && {
          attachment: {
            type: isImage ? 'image' : 'file',
            url: `data:${file.mimeType};base64,${file.data}`,
            mimeType: file.mimeType,
            data: file.data,
            name: file.name?.trim() || (isImage ? 'Image' : 'Document'),
          },
        }),
      }

      if (!skipUserAppend) {
        setTranscript(prev => {
          const lastItem = prev[prev.length - 1]
          const isConsecutiveDuplicate =
            lastItem?.role === 'user' &&
            lastItem.text === userItem.text &&
            (lastItem.attachment?.name ?? null) === (userItem.attachment?.name ?? null)
          if (isConsecutiveDuplicate) return prev
          return [...prev, userItem]
        })
      }

      // Live-down / recentlyLive / missing sendText refuse REST and would leave
      // a book ask sitting with no assistant row. The card is deterministic.
      const surfaceDeterministicBooking = (reason: string): boolean => {
        if (!isExplicitBookingAction(text)) return false
        logger.info('[useMessageComposer] Live refused the book ask — local Schedule card', {
          reason,
        })
        setTranscript(prev => appendBookingReply(prev))
        currentLoadingIdRef.current = null
        setIsThinking(false)
        setBackendStatus({
          mode: 'idle',
          message: t('composerStatus.scheduleReady', { lng: uiNow }),
          severity: 'info',
        })
        return true
      }

      // Live is the sole writer while connected.
      // File-picker on iOS/Safari can drop CONNECTED→DISCONNECTED before send;
      // sticky lastLiveHeldAt keeps PDF on Fly instead of REST-falling-through.
      // The reply does not wait on GET /api/session. A lookup that never
      // returns used to sit in front of this send.
      const micOpenAtSend = isLiveMicOpen(liveServiceRef.current)
      const recentlyLive =
        lastLiveHeldAtRef.current > 0 && Date.now() - lastLiveHeldAtRef.current < 30_000
      const liveHeldAtSend =
        isLiveRuntimeBlockingRest(connectionStateRef.current, liveServiceRef.current) ||
        micOpenAtSend ||
        recentlyLive
      if (sessionId && !liveHeldAtSend) {
        void persistMessageToServer(sessionId, 'user', text, userTimestamp, file)
      }
      const activeSessionId = clientSessionViewStore.getSnapshot().sessionId || sessionId
      // A line the browser is still storing (intake exchange, intake record) must land
      // before this one goes anywhere. Otherwise this line loses the race and is dropped.
      await canonicalStoreQueueRef.current.settled()

      // Same inventory as the Discovery Brief card. Do not let Live invent
      // "no remaining discovery questions" while the card still lists gaps.
      if (!file && isRemainingDiscoveryAsk(text)) {
        const readiness = getSessionFactsReadiness(
          clientSessionViewStore.getServerProjectionSnapshot()?.projection.sessionFacts,
        )
        const reply = formatRemainingDiscoveryReply(readiness, uiNow)
        const replyTimestamp = new Date()
        setTranscript(prev => [
          ...prev,
          {
            id: generateTranscriptId('assistant'),
            role: 'model',
            text: reply,
            timestamp: replyTimestamp,
            isFinal: true,
            status: 'complete',
          },
        ])
        if (sessionId) {
          void canonicalStoreQueueRef.current.enqueue(() => appendCanonicalExchange(sessionId, text, reply)).catch(error => {
            logger.warn('[useMessageComposer] Discovery line was not stored', {
              error: error instanceof Error ? error.message : String(error),
            })
            setBackendStatus({
              mode: 'idle',
              message: t('composerStatus.lineNotStored', { lng: uiNow }),
              severity: 'warn',
            })
          })
          void persistMessageToServer(sessionId, 'model', reply, replyTimestamp)
        }
        currentLoadingIdRef.current = null
        setIsThinking(false)
        setBackendStatus({
          mode: 'idle',
          message: t('composerStatus.discoveryCoverage', { lng: uiNow }),
          severity: 'info',
        })
        inFlightSendKeysRef.current.delete(dedupeKey)
        return
      }

      if (!file && intakePathForTurn('text') === 'local') {
        const projection = clientSessionViewStore.getServerProjectionSnapshot()?.projection
        const researchBlob = [projection?.company?.summary, projection?.company?.name]
          .filter((line): line is string => Boolean(line && line.trim()))
          .join('\n')
        const language = languageAfterResearch(
          intakeLanguageForTurn(text, workshopIntakeRef.current?.language ?? null),
          researchBlob
        )
        const email = projection?.visitor?.email ?? null
        const hintedName = projection?.company?.name ?? null
        const rawPlate = userProfileRef.current?.company
        const plateCompany = typeof rawPlate === 'string' ? rawPlate.trim() : ''
        const decision = decideCompanyBinding({
          email,
          ...(plateCompany.length >= 2 ? { plateCompany } : {}),
          candidate: hintedName
            ? {
                name: hintedName,
                ...(projection?.company?.domain ? { domain: projection.company.domain } : {}),
                ...(typeof projection?.company?.confidence === 'number'
                  ? { confidence: projection.company.confidence }
                  : {}),
              }
            : null,
        })
        const boundName =
          decision.status === 'bound' ? decision.companyName : companyNameFromEmail(email)
        const known = intakeFieldsFromResearchContext(
          projection?.researchContext,
          projection?.company ?? null,
          language
        )
        const briefFacts = intakeFieldsFromSessionFacts(projection?.sessionFacts, language)
        const turned = advanceWorkshopIntake({
          intake: workshopIntakeRef.current,
          userText: text,
          now: new Date().toISOString(),
          language,
          known,
          briefFacts,
          companyName: boundName,
          ...(plateCompany.length >= 2 ? { plateCompany } : {}),
          email,
        })
        if (turned.handled && turned.reply) {
          workshopIntakeRef.current = turned.intake
          const replyTimestamp = replyTimestampAfter(userTimestamp)
          setTranscript(prev => [
            ...prev,
            {
              id: generateTranscriptId('assistant'),
              role: 'model',
              text: turned.reply as string,
              timestamp: replyTimestamp,
              isFinal: true,
              status: 'complete',
            },
          ])
          if (activeSessionId) {
            const reply = turned.reply
            const intakeToStore = shouldPersistWorkshopIntake(turned.intake) ? turned.intake : null
            // One after the other: the exchange, then the intake record on the fresh version.
            void canonicalStoreQueueRef.current.enqueue(async () => {
              await storeExchangeThenRefresh(
                () => appendCanonicalExchange(activeSessionId, text, reply),
                () => persistMessageToServer(activeSessionId, 'model', reply, replyTimestamp),
              ).catch(error => {
                logger.warn('[useMessageComposer] Intake line was not stored', {
                  error: error instanceof Error ? error.message : String(error),
                })
                setBackendStatus({
                  mode: 'idle',
                  message: t('composerStatus.lineNotStored', { lng: turned.intake.language === 'en' ? 'en' : 'nb' }),
                  severity: 'warn',
                })
              })
              if (!intakeToStore) return
              await submitWorkshopIntake(activeSessionId, intakeToStore).catch(error => {
                logger.warn('[useMessageComposer] Workshop intake was not stored', {
                  error: error instanceof Error ? error.message : String(error),
                })
                setBackendStatus({
                  mode: 'idle',
                  message: WORKSHOP_CUSTOMER_ERROR[intakeToStore.language],
                  severity: 'warn',
                })
              })
            })
          }

          currentLoadingIdRef.current = null
          setIsThinking(false)
          setBackendStatus({
            mode: 'idle',
            message: t('composerStatus.workshopIntake', { lng: uiNow }),
            severity: 'info',
          })
          inFlightSendKeysRef.current.delete(dedupeKey)
          return
        }
      }

      if (!file && isDiscoveryReportRequest(text)) {
        logger.info('[useMessageComposer] Auto-running discovery brief (progress UI) before Live/REST', {
          sessionPrefix: sessionIdPrefix(sessionId),
        })
        const briefRun = Promise.resolve(handleGenerateDiscoveryReport(undefined, 'auto') as unknown)
        // Offline/REST: client prepare is the product action — do not also POST chat.
        // Live-connected: still sendText so the model must-call generate_discovery_report.
        if (!liveHeldAtSend) {
          // #317 E2E 317d (sess_614697cd): this line and the brief reply were never written to
          // the session, so admin missed the first exchange. Live writes its own turns; here the
          // browser does, once the brief is made (no reply when it was not).
          if (activeSessionId) {
            const replyText = String(t('toast.reportGenerated', { lng: uiNow }))
            void briefRun
              .catch(() => false)
              .then(made =>
                canonicalStoreQueueRef.current.enqueue(() =>
                  appendCanonicalExchange(activeSessionId, text, made === true ? replyText : ''),
                ),
              )
              .catch(error => {
                logger.warn('[useMessageComposer] Brief request line was not stored', {
                  error: error instanceof Error ? error.message : String(error),
                })
                setBackendStatus({
                  mode: 'idle',
                  message: t('composerStatus.lineNotStored', { lng: uiNow }),
                  severity: 'warn',
                })
              })
          }
          inFlightSendKeysRef.current.delete(dedupeKey)
          return
        }
      }

      if (!opts?.skipResearch) {
        const explicitRequest = /\b(research|look up|lookup|background check|enrich)\b/i.test(text)
        const containsEmail = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/.test(text)
        if (explicitRequest && (containsEmail || userProfileRef.current?.email)) {
          void performResearch(text)
        }
      }

      // Typed «Hvem er du?» rides Fly when the socket is up, and Fly paraphrases
      // the prefix into «Farzad Bayats KI». Answer the bare nb ask here.
      const identityReply = !file ? deterministicNbIdentityReply(text) : null
      if (identityReply) {
        const replyTimestamp = new Date()
        setTranscript(prev => [
          ...prev,
          {
            id: generateTranscriptId('assistant'),
            role: 'model',
            text: identityReply,
            timestamp: replyTimestamp,
            isFinal: true,
            status: 'complete',
          },
        ])
        if (sessionId) {
          void canonicalStoreQueueRef.current
            .enqueue(() => appendCanonicalExchange(sessionId, text, identityReply))
            .catch(error => {
              logger.warn('[useMessageComposer] Identity line was not stored', {
                error: error instanceof Error ? error.message : String(error),
              })
              setBackendStatus({
                mode: 'idle',
                message: t('composerStatus.lineNotStored', { lng: uiNow }),
                severity: 'warn',
              })
            })
          void persistMessageToServer(sessionId, 'model', identityReply, replyTimestamp)
        }
        currentLoadingIdRef.current = null
        setIsThinking(false)
        setBackendStatus({ mode: 'idle', message: identityReply, severity: 'info' })
        inFlightSendKeysRef.current.delete(dedupeKey)
        return
      }

      // Visitor chat always rides Fly Live. Connect/reconnect without audio —
      // mic-off / webcam-off must not fall through to POST /api/chat/unified.
      const liveFirstFunnel = isLiveFirstFunnelEnabled()
      let isLiveConnected = isLiveRuntimeConnected(
        connectionStateRef.current,
        liveServiceRef.current
      )

      if (!isLiveConnected && handleConnect) {
        setBackendStatus({
          mode: 'voice',
          message: t(LIVE_FIRST_SEND_STATUS_KEYS.connecting),
          severity: 'info',
        })
        try {
          await handleConnect({ audio: false })
          isLiveConnected = isLiveRuntimeConnected(
            connectionStateRef.current,
            liveServiceRef.current
          )
        } catch (connectError) {
          logger.warn('[useMessageComposer] Live connect/reconnect failed', {
            error: connectError instanceof Error ? connectError.message : String(connectError),
            liveHeldAtSend,
          })
          showToast(t(LIVE_FIRST_FALLBACK_MESSAGE_KEY), 'info')
          logTurnObservability({
            turnId: userItem.id,
            sessionId,
            runtimeOwner: 'live',
            turnOwner: 'live',
            visibleAnswerOwner: 'live',
            route: 'live',
            detail: 'live_connect_failed',
          })
        }
      }

      // Wait out CONNECTING before routing. sendText while CONNECTING hits LIVE_GATEWAY_NOT_OPEN.
      // Do not fold CONNECTING into isLiveRuntimeConnected — that helper means "socket can take a turn now".
      let liveBecameReadyWhileConnecting = false
      if (connectionStateRef.current === LiveConnectionState.CONNECTING) {
        const ready = await liveServiceRef.current?.waitForSessionReady?.()
        liveBecameReadyWhileConnecting = ready === true
      }

      // INVARIANT: once Live owns a session, typed turns stay on Live (no hybrid REST exceptions).
      const liveService = liveServiceRef.current
      const liveOwnsTurns = liveRuntimeOwnsTurns(liveService)
      const mediaActive = isWebcamActive || isScreenShareActive
      isLiveConnected =
        isLiveRuntimeConnected(connectionStateRef.current, liveService) ||
        liveBecameReadyWhileConnecting ||
        micOpenAtSend

      logger.debug('[useMessageComposer] route decision', {
        route: isLiveConnected ? 'live' : 'rest',
        liveFirstFunnel,
        liveOwnsTurns,
        mediaActive,
        ...getLiveRuntimeDiagnostics(connectionState, liveService),
      })

      // If Live dropped, we already tried reconnect above. Never release to REST.
      if (
        liveOwnsTurns &&
        !isLiveConnected &&
        connectionStateRef.current !== LiveConnectionState.CONNECTING
      ) {
        logger.warn('[useMessageComposer] Live not connected after reconnect attempt — refusing REST', {
          ...getLiveRuntimeDiagnostics(connectionState, liveService),
        })
      }

      const shouldUseVoice =
        isLiveConnected && liveServiceRef.current?.getIsAudioInputActive() === true
      const resolvedRoute = shouldUseVoice ? 'voice-live' : isLiveConnected ? 'live' : 'rest'
      logTurnObservability({
        turnId: userItem.id,
        sessionId,
        runtimeOwner: isLiveConnected ? 'live' : liveFirstFunnel ? 'fallback' : 'rest',
        turnOwner: isLiveConnected ? 'live' : 'rest',
        visibleAnswerOwner: isLiveConnected ? 'live' : 'rest',
        route: resolvedRoute,
      })
      liveRuntimeTrace({
        event: 'route_decision',
        route: resolvedRoute,
        runtimeOwner:
          typeof liveService?.getRuntimeOwnershipState === 'function'
            ? liveService.getRuntimeOwnershipState().reason
            : undefined,
        sessionPrefix: sessionIdPrefix(sessionId),
        textLen: text.length,
        textPreview: text.slice(0, 56),
        extra: {
          liveFirstFunnel,
          liveOwnsTurns,
          isLiveConnected,
        },
      })
      const isVisualRecall = isVisualRecallQuestion(text)
      const deterministicVisualFallbackReason = getDeterministicVisualFallbackReason(
        text,
        isWebcamActive,
        isScreenShareActive
      )

      setLivePolicy(prev => {
        if (!voiceTurn) {
          const code = turnReply === 'en' ? 'en-US' : turnReply === 'nn' ? 'nn-NO' : 'nb-NO'
          return {
            ...prev,
            languageCode: code,
            affectiveState: inferAffectiveStateFromText(text, prev.affectiveState),
          }
        }
        const resolved = resolveStickyReplyLanguage(
          text,
          prev.languageCode,
          languageStreakRef.current,
        )
        languageStreakRef.current = resolved.streak
        if (resolved.languageCode !== prev.languageCode) {
          const switched = resolved.languageCode.startsWith('en') ? 'en' : 'nb'
          setLocale(switched, { explicit: true })
        }
        return {
          ...prev,
          languageCode: resolved.languageCode,
          affectiveState: inferAffectiveStateFromText(text, prev.affectiveState),
        }
      })

      if (shouldUseVoice) {
        setBackendStatus({
          mode: 'voice',
          message: file
            ? isLiveRealtimeMediaMime(file.mimeType)
              ? t('composerStatus.voiceMedia', { lng: uiNow })
              : t('composerStatus.voiceFile', { lng: uiNow })
            : t('composerStatus.voiceConnected', { lng: uiNow }),
          severity: 'info',
        })
        if (file && isLiveRealtimeMediaMime(file.mimeType)) {
          liveServiceRef.current?.sendRealtimeMedia(file)
        }
      } else if (isLiveConnected) {
        setBackendStatus({
          mode: 'voice',
          message: file
            ? t('composerStatus.liveFile', { lng: uiNow })
            : t('composerStatus.liveText', { lng: uiNow }),
          severity: 'info',
        })
      }

      if (isLiveConnected) {
        if (deterministicVisualFallbackReason) {
          const unavailableMessage = deterministicVisualFallbackReason === 'screen_intent_no_frame'
            ? t('visualFallback.screenNoFrame', { lng: uiNow })
            : t('visualFallback.webcamNoFrame', { lng: uiNow })

          const fallbackId = generateTranscriptId('assistant')
          const fallbackTimestamp = new Date()
          setTranscript(prev => [
            ...prev,
            {
              id: fallbackId,
              role: 'model',
              text: unavailableMessage,
              timestamp: fallbackTimestamp,
              isFinal: true,
              status: 'complete',
            },
          ])
          setBackendStatus({ mode: 'idle', message: unavailableMessage, severity: 'info' })
          currentLoadingIdRef.current = null
          setIsThinking(false)

          if (sessionId && !isLiveConnected) {
            void persistMessageToServer(sessionId, 'model', unavailableMessage, fallbackTimestamp)
          }

          if (liveServiceRef.current?.getIsAudioInputActive?.()) {
            liveServiceRef.current.speakAgentOutput?.(unavailableMessage)
          }

          return
        }

        if (file && isLiveRealtimeMediaMime(file.mimeType)) {
          liveServiceRef.current?.sendRealtimeMedia(file)
        }

        const liveVisualDirective = isVisualRecall ? '' : buildLiveVisualDirective(text)
        const sendText =
          typeof liveService?.sendText === 'function'
            ? liveService.sendText.bind(liveService)
            : typeof liveServiceRef.current?.sendText === 'function'
              ? liveServiceRef.current.sendText.bind(liveServiceRef.current)
              : null

        if (!sendText) {
          logger.warn('[useMessageComposer] Live connected but sendText unavailable; refusing REST')
          if (surfaceDeterministicBooking('sendText_missing')) return
          setBackendStatus({
            mode: 'voice',
            message: t('composerStatus.liveTextUnavailable', { lng: uiNow }),
            severity: 'warn',
          })
          return
        }

        setBackendStatus({
          mode: 'voice',
          message: t(LIVE_FIRST_SEND_STATUS_KEYS.sending),
          severity: 'info',
        })

        // Show thinking/processing chrome immediately on Live typed turns.
        // REST gate sets this in useLiveGateTurnHandler; Live-first sendText did not,
        // so visitors saw dead air until the first stream token.
        setIsThinking(true)

        // Only claim REST-gate ownership for forced funnel intents. Otherwise
        // restTurnOwnerRef suppresses native Live transcripts and the 60s
        // gate timeout paints ORCHESTRATOR_FALLBACK.
        if (detectLiveFirstForcedGate(text).forced && beginGateTurn) {
          beginGateTurn()
        }

        const liveTurnText = liveOwnedTurnText(enhancedText, file)
        sendText(
          liveVisualDirective ? `${liveVisualDirective}\\n\\n${liveTurnText}` : liveTurnText,
          { recordTranscript: false }
        )

        setBackendStatus({
          mode: 'agents',
          message: t(LIVE_FIRST_SEND_STATUS_KEYS.receiving),
          severity: 'info',
        })

        return
      }

      if (
        liveHeldAtSend ||
        isLiveRuntimeBlockingRest(connectionStateRef.current, liveServiceRef.current) ||
        micOpenAtSend
      ) {
        if (!file && surfaceDeterministicBooking('live_held_refuse_rest')) return
        if (connectionStateRef.current === LiveConnectionState.CONNECTING) {
          logger.warn('[useMessageComposer] Live still CONNECTING after wait; refusing REST', {
            ...getLiveRuntimeDiagnostics(connectionStateRef.current, liveServiceRef.current),
          })
        } else if (file) {
          logger.warn('[useMessageComposer] refusing REST/SSE for file while Live/mic is up', {
            ...getLiveRuntimeDiagnostics(connectionStateRef.current, liveServiceRef.current),
            micOpenAtSend,
            liveHeldAtSend,
          })
          setBackendStatus({
            mode: 'voice',
            message: t('composerStatus.voiceFileStays', { lng: uiNow }),
            severity: 'warn',
          })
        } else {
          logger.warn('[useMessageComposer] refusing REST/SSE while Live is connected', {
            ...getLiveRuntimeDiagnostics(connectionStateRef.current, liveServiceRef.current),
          })
        }
        // The typed line is on screen but was not sent (E2E 318 run B). Ask for a resend.
        if (!file) showToast(t('composerStatus.lineNotStored', { lng: uiNow }), 'error')
        return
      }

      logger.warn('[useMessageComposer] refusing REST/SSE for visitor chat; Live is the only conversation writer', {
        ...getLiveRuntimeDiagnostics(connectionStateRef.current, liveServiceRef.current),
        liveHeldAtSend,
        micOpenAtSend,
      })
      if (surfaceDeterministicBooking('live_only_writer')) return
      setBackendStatus({
        mode: 'voice',
        message: t('composerStatus.liveUnavailable', { lng: uiNow }),
        severity: 'warn',
      })
      // The line stays on screen but never reached the server (E2E 318 run B). Ask for a resend.
      showToast(t('composerStatus.lineNotStored', { lng: uiNow }), 'error')
      return
      } finally {
        inFlightSendKeysRef.current.delete(dedupeKey)
      }

    },
    [
      connectionState,
      sessionId,
      setBackendStatus,
      showToast,
      persistMessageToServer,
      liveServiceRef,
      setTranscript,
      isWebcamActive,
      isScreenShareActive,
      performResearch,
      setIsThinking,
      handleGenerateDiscoveryReport,
      transcriptRef,
      currentLoadingIdRef,
      inFlightSendKeysRef,
      lastSendRef,
      lastTextInputRef,
      userProfileRef,
      abortControllerRef,
      setLivePolicy,
      inferAffectiveStateFromText,
      setLocale,
      languageCode,
      t,
      beginGateTurn,
      handleConnect,
    ]
  )

  const handleSendMessageRef = useRef<typeof handleSendMessage | null>(null)
  const prevLiveConnectedRef = useRef(
    isLiveRuntimeConnected(connectionState, liveServiceRef.current)
  )

  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage
  }, [handleSendMessage])

  useEffect(() => {
    const connected = isLiveRuntimeConnected(connectionState, liveServiceRef.current)
    const wasConnected = prevLiveConnectedRef.current
    prevLiveConnectedRef.current = connected
    if (!connected || wasConnected) return
    const controller = abortControllerRef.current
    if (!controller || controller.signal.aborted) return
    logger.warn('[useMessageComposer] aborting in-flight REST/SSE because Live connected', {
      ...getLiveRuntimeDiagnostics(connectionState, liveServiceRef.current),
    })
    cleanupLiveSessionGenerationState({
      setTranscript,
      setIsThinking,
      currentLoadingIdRef,
      ...(restTurnOwnerRef ? { restTurnOwnerRef } : {}),
      abortControllerRef,
    })
  }, [
    abortControllerRef,
    connectionState,
    currentLoadingIdRef,
    liveServiceRef,
    restTurnOwnerRef,
    setIsThinking,
    setTranscript,
  ])

  // P3: Cleanup report timeout on unmount
  useEffect(() => {
    return () => {
      if (reportTimeoutRef.current) {
        clearTimeout(reportTimeoutRef.current)
        reportTimeoutRef.current = null
      }
    }
  }, [])

  return {
    handleSendMessage,
    handleSendMessageRef,
  }
}
